import React, {
    useEffect,
    useContext,
    useReducer,
    useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
    makeStyles, Button, Grid,
} from '@material-ui/core';
import { modules } from 'utils/enum/modules';
import { v1 as uuid } from 'uuid';
import { FetchPolicy } from 'utils/enum/Core';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import DataType from 'utils/enum/DataType';
import UserContext from 'components/context/UserContext';
import InventoryQuery from 'services/graphQL/query/InventoryQuery';
import InventoryMutation from 'services/graphQL/mutate/InventoryMutation';
import ServiceSettingsQuery from 'services/graphQL/query/service/ServiceSettings';
import ServiceSettingsMutation from 'services/graphQL/mutate/service/ServiceSettingsMutation';
import DealsSettingsQuery from 'services/graphQL/query/DealsSettingsQuery';
import DealsSettingsMutate from 'services/graphQL/mutate/DealsSettingsMutate';
import { Col, Form } from 'react-bootstrap';
import StringUtils from 'lib/StringUtils';
import ModalUtils from 'utils/ModalUtils';
import { InventoryCategory } from 'utils/enum/InventoryEnum';
import LotQuery from 'services/graphQL/query/LotQuery';
import LotMutation from 'services/graphQL/mutate/LotMutation';
import LotsCategory from 'utils/enum/LotsCategory';
import ButtonStyles from 'styles/theme/Button';
import VirtualTable from 'components/widgets/VirtualTable';
import InputNumber from 'components/widgets/InputNumber';
import If from 'components/widgets/conditional/If';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import clsx from 'clsx';

const INIT_STATE = {
    confirmDialogOpen: false,
    invUserTabName1: '',
    invUserTabName2: '',
    customFields: [],
    packsCustomFields: [],
    lots: [],
    lotsSettings: [],
    selectedLot: null,
};

const ACTION_TYPES = {
    SET_TABS_NAME: 'setTabsName',
    SET_CUSTOM_FIELDS: 'setCustomFields',
    SET_PACKS_CUSTOM_FIELDS: 'setPacksCustomFields',
    SET_CUSTOM_FIELD: 'setCustomField',
    SET_LOTS: 'setLots',
    SET_LOT: 'setLot',
    TOGGLE_CONFIRM_DIALOG: 'toggleConfirmDialog',
    SET_DEFAULT_VALUE: 'setDefaultValue',
    SET_DEFAULT_LABEL: 'setDefaultLabel',
    SET_LOT_SETTINGS: 'setLotsSettings',
};

const reducer = (state, action = {}) => {
    const {
        selectedLot,
        customFields,
        packsCustomFields,
        lotsSettings,
    } = state;
    const { defaultValue, defaultLabel, entity } = action?.value ?? {};

    switch (action.type) {
    case ACTION_TYPES.SET_TABS_NAME:
        const { value } = action;

        return {
            ...state,
            invUserTabName1: value.invUserTabName1 || '',
            invUserTabName2: value.invUserTabName2 || '',
        };
    case ACTION_TYPES.SET_CUSTOM_FIELDS:
        return {
            ...state,
            customFields: action.value,
        };
    case ACTION_TYPES.SET_PACKS_CUSTOM_FIELDS:
        return {
            ...state,
            packsCustomFields: [
                {
                    displayed: true,
                    entityMetadataId: uuid(),
                    label: 'Pack',
                    property: 'PackCustomField0',
                    type: 'MONEY',
                    notCustomField: true,
                    defaultValues: (lotsSettings ?? []).map((s) => ({
                        defaultValue: s.packCost,
                        lotId: s.lotId,
                    })),
                },
                ...action.value,
            ],
        };
    case ACTION_TYPES.SET_LOT_SETTINGS:
        return {
            ...state,
            lotsSettings: action.value,
        };
    case ACTION_TYPES.SET_CUSTOM_FIELD:
        const { index, customField } = action.value;
        const clone = [...(action.value.isPacks ? state.packsCustomFields : state.customFields)];

        clone[index] = customField;
        return {
            ...state,
            [action.value.isPacks ? 'packsCustomFields' : 'customFields']: clone,
        };
    case ACTION_TYPES.SET_LOTS:
        const lots = action.value;
        return {
            ...state,
            ...(lots?.length > 0 ? {
                selectedLot: lots[0].lotId,
            } : {}),
            lots,
        };
    case ACTION_TYPES.SET_LOT:
        return {
            ...state,
            selectedLot: action.value,
        };
    case ACTION_TYPES.SET_DEFAULT_LABEL:
        return {
            ...state,
            packsCustomFields: packsCustomFields.map((i) => {
                if (i.entityMetadataId === entity) {
                    const defaultClone = [...(i.defaultValues ?? [])];
                    const defaultItemIndex = defaultClone.findIndex((x) => x.lotId === selectedLot);

                    if (defaultItemIndex >= 0) {
                        defaultClone[defaultItemIndex] = {
                            ...defaultClone[defaultItemIndex],
                            label: defaultLabel,
                        };
                    } else {
                        defaultClone.push({
                            lotId: selectedLot,
                            label: defaultLabel,
                        });
                    }

                    return {
                        ...i,
                        defaultValues: defaultClone,
                    };
                }

                return i;
            }),
        };
    case ACTION_TYPES.SET_DEFAULT_VALUE:
        return {
            ...state,
            [action.value.isPacks ? 'packsCustomFields' : 'customFields']: (action.value.isPacks ? packsCustomFields : customFields).map((i) => {
                if (i.entityMetadataId === entity) {
                    const defaultClone = [...(i.defaultValues ?? [])];
                    const defaultItemIndex = defaultClone.findIndex((x) => x.lotId === selectedLot);

                    if (defaultItemIndex >= 0) {
                        defaultClone[defaultItemIndex] = {
                            ...defaultClone[defaultItemIndex],
                            defaultValue,
                        };
                    } else {
                        defaultClone.push({
                            lotId: selectedLot,
                            defaultValue,
                        });
                    }

                    return {
                        ...i,
                        defaultValues: defaultClone,
                    };
                }

                return i;
            }),
        };
    case ACTION_TYPES.TOGGLE_CONFIRM_DIALOG:
        return {
            ...state,
            confirmDialogOpen: !state.confirmDialogOpen,
        };
    default:
        return state;
    }
};

const buttonStyles = makeStyles((theme) => ButtonStyles.getStyle(theme));
const useStyles = makeStyles((theme) => ({
    box: {
        marginTop: '10px',
        paddingTop: '10px',
        border: `solid 1px ${theme.palette.border.mercury}`,
    },
    labels: {
        fontSize: '0.875rem',
        fontWeight: 'bold',
    },
    subLabel: {
        fontSize: '12px',
    },
    input: {
        fontSize: '14px',
    },
    customTabsSave: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    customFieldsSave: {
        display: 'flex',
        justifyContent: 'flex-end',
        [theme.breakpoints.down('sm')]: {
            marginBottom: 'initial',
        },
    },
    tableContainer: {
        height: '260px',
        overflow: 'hidden',
        [theme.breakpoints.down('md')]: {
            overflowY: 'hidden',
            overflowX: 'auto',
            '& > div': {
                minWidth: '600px',
            },
        },
    },
    tableHeader: {
        textAlign: 'left',
        color: theme.palette.text.waterloo,
        borderRight: `1px solid ${theme.palette.border.ghost}`,
        height: '100%',
        alignItems: 'center',
    },
    lotSelector: {
        display: 'flex',
        alignItems: 'baseline',
        '& > div': {
            marginLeft: '5px',
            width: '150px',
            '& > div > div': {
                fontSize: '12px',
            },
        },
    },
    packs: {
        display: 'flex',
        justifyContent: 'space-between',
        paddingLeft: '15px',
        alignItems: 'center',
        paddingRight: '15px',
        marginBottom: '10px',
        '& > label': {
            marginBottom: 0,
        },
    },
    packsMessage: {
        paddingLeft: '15px',
        fontSize: '13px',
    },
}));

const CustomFieldsSettings = ({ module, writable }) => {
    const classes = { ...useStyles(), ...buttonStyles() };
    const { userInformation, refetchUserInformation } = useContext(UserContext);
    const [state, dispatch] = useReducer(reducer, INIT_STATE);
    const tempCustomFieldData = useRef();

    const {
        confirmDialogOpen,
        invUserTabName1,
        invUserTabName2,
        customFields,
        packsCustomFields,
        selectedLot,
        lotsSettings,
        lots,
    } = state;

    const isInventory = module === modules.INVENTORY;
    const isService = module === modules.SERVICE;
    const isDeals = module === modules.DEALS;

    let query = null;
    let mutation = null;
    if (isInventory) {
        query = InventoryQuery.GET_INVENTORY_METADATA;
        mutation = InventoryMutation.SAVE_INVENTORY_METADATA;
    }

    if (isService) {
        query = ServiceSettingsQuery.GET_SERVICE_METADATA;
        mutation = ServiceSettingsMutation.SAVE_SERVICE_METADATA;
    }

    if (isDeals) {
        query = DealsSettingsQuery.GET_DEALS_METADATA;
        mutation = DealsSettingsMutate.SAVE_DEALS_METADATA;
    }

    const {
        data: metaData,
        loading: metaDataLoading,
        error: metaDataError,
    } = useQuery(query, {
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const {
        data: packsMetadata,
        loading: packsMetadataLoading,
        error: packsMetadataError,
    } = useQuery(InventoryQuery.GET_INVENTORY_PACKS_METADATA, {
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        skip: !isInventory || (isInventory && lotsSettings?.length === 0),
    });

    const {
        data: lotsData,
        loading: lotsLoading,
        error: lotsError,
    } = useQuery(LotQuery.GET_LOTS, {
        variables: {
            filter: {
                active: true,
            },
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const {
        data: lotsSettingsData,
        loading: lotsSettingsLoading,
        error: lotsSettingsError,
        refetch: refetchLotSettings,
    } = useQuery(LotQuery.GET_LOT_SETTINGS, {
        variables: {
            filter: {
                active: true,
            },
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const [saveUserTabNames] = useMutation(InventoryMutation.SAVE_INVENTORY_SETTINGS, {
        onCompleted: (response) => {
            if (response) {
                ModalUtils.successMessage(null, 'User Tab Names saved successfully');
                return;
            }

            ModalUtils.errorMessage(null, 'There was an error trying to save the User Tab Names');
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage([errorMessage]);
        },
    });

    const [saveMetadata] = useMutation(mutation, {
        onCompleted: (response) => {
            if (response) {
                ModalUtils.successMessage(null, 'Custom Fields saved successfully');
            }
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage([errorMessage]);
        },
    });

    const [savePacksMetadata] = useMutation(InventoryMutation.SAVE_INVENTORY_PACKS_METADATA, {
        onCompleted: (response) => {
            if (response) {
                refetchLotSettings();
                ModalUtils.successMessage(null, 'Packs Custom Fields saved successfully');
            }
        },
        onError: (errorMessage) => {
            ModalUtils.errorMessage([errorMessage]);
        },
    });

    const [saveLotPackCost] = useMutation(LotMutation.UPDATE_LOT_PACK_COST, {
        onError: (errorMessage) => {
            ModalUtils.errorMessage([errorMessage]);
        },
    });

    const [getSettings] = useLazyQuery(LotQuery.GET_SETTINGS, {
        onCompleted: (response) => {
            if (response) {
                const settings = response.getSettings;
                dispatch({
                    type: ACTION_TYPES.SET_TABS_NAME,
                    value: settings.reduce((a, b) => {
                        const current = { ...a };
                        current[b.key] = b.value;

                        return current;
                    }, {}),
                });
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage([error]);
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const { INV_USER_TAB_NAME_1, INV_USER_TAB_NAME_2 } = InventoryCategory;
    useEffect(() => {
        if (userInformation) {
            const defaultLot = userInformation?.defaultLot || userInformation?.usersLot;

            getSettings({
                variables: {
                    category: LotsCategory.INVENTORY,
                    lotName: defaultLot,
                    key: [INV_USER_TAB_NAME_1, INV_USER_TAB_NAME_2],
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInformation]);

    useEffect(() => {
        if (lotsSettingsError) {
            ModalUtils.errorMessage(lotsSettingsError?.graphQLErrors);
            return;
        }

        if (!lotsSettingsLoading && lotsSettingsData) {
            const { lotList } = lotsSettingsData;
            dispatch({
                type: ACTION_TYPES.SET_LOT_SETTINGS,
                value: lotList,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lotsSettingsLoading, lotsSettingsError]);

    useEffect(() => {
        if (packsMetadataError) {
            ModalUtils.errorMessage(packsMetadataError?.graphQLErrors);
            return;
        }

        if (!packsMetadataLoading && packsMetadata) {
            const { getInventoryPacksMetadata } = packsMetadata;
            dispatch({
                type: ACTION_TYPES.SET_PACKS_CUSTOM_FIELDS,
                value: getInventoryPacksMetadata,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packsMetadataError, packsMetadataLoading]);

    useEffect(() => {
        if (metaDataError) {
            ModalUtils.errorMessage(metaDataError?.graphQLErrors);
            return;
        }

        if (!metaDataLoading) {
            const { getInventoryMetadata, getServiceMetadata, getDealsMetadata } = metaData;
            dispatch({
                type: ACTION_TYPES.SET_CUSTOM_FIELDS,
                value: getInventoryMetadata ?? getServiceMetadata ?? getDealsMetadata,
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metaDataError, metaDataLoading]);

    useEffect(() => {
        if (lotsError) {
            ModalUtils.errorMessage(lotsError?.graphQLErrors);
            return;
        }

        if (!lotsLoading) {
            dispatch({
                type: ACTION_TYPES.SET_LOTS,
                value: lotsData?.lotList ?? [],
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lotsError, lotsLoading]);

    const onChange = ({ target: { name, value, entity } }, skipConfirm = false, subSection) => {
        if (name.startsWith('invUserTabName')) {
            dispatch({
                type: ACTION_TYPES.SET_TABS_NAME,
                value: {
                    invUserTabName1,
                    invUserTabName2,
                    [name]: value,
                },
            });

            return;
        }

        if (name.startsWith('selectedLot')) {
            dispatch({
                type: ACTION_TYPES.SET_LOT,
                value,
            });

            return;
        }

        const isPacks = subSection === 'packs';
        if (name.startsWith('defaultLabel')) {
            dispatch({
                type: ACTION_TYPES.SET_DEFAULT_LABEL,
                value: {
                    defaultLabel: value,
                    entity,
                },
            });

            return;
        }

        if (name.startsWith('defaultValue')) {
            dispatch({
                type: ACTION_TYPES.SET_DEFAULT_VALUE,
                value: {
                    defaultValue: value,
                    entity,
                    isPacks,
                },
            });

            return;
        }

        const property = name.split('-')[0];
        const field = name.split('-')[1];
        const index = (isPacks ? packsCustomFields : customFields).findIndex((cf) => cf.property === property);
        const customField = { ...((isPacks ? packsCustomFields : customFields)[index]) };
        const hasData = customField?.hasData;

        if (field === 'type' && hasData && !skipConfirm) {
            dispatch({
                type: ACTION_TYPES.TOGGLE_CONFIRM_DIALOG,
            });

            tempCustomFieldData.current = { name, value };
            return;
        }

        customField[field] = !['displayed', 'active', 'taxable'].includes(field) ? value : !customField[field];

        dispatch({
            type: ACTION_TYPES.SET_CUSTOM_FIELD,
            value: {
                index,
                customField,
                isPacks,
            },
        });
    };

    const onConfirmDialogClose = () => {
        dispatch({
            type: ACTION_TYPES.TOGGLE_CONFIRM_DIALOG,
        });
    };

    const saveCustomFields = async (subSection) => {
        const input = [];
        let failure = false;

        const isPacks = subSection === 'packs';
        const fields = isPacks ? packsCustomFields.filter(({ notCustomField = false }) => !notCustomField) : customFields;

        for (let i = 0; i < fields.length; i += 1) {
            const {
                entityMetadataId,
                label,
                type,
                displayed,
                active = isDeals || isInventory,
                taxable = false,
                defaultValues = [],
            } = fields[i];

            if (
                ((isInventory || isDeals) && displayed && !isPacks && StringUtils.isEmpty(label))
                || (isService && active && StringUtils.isEmpty(label))
            ) {
                failure = true;
                ModalUtils.errorMessage(null, 'Please make sure a label is set for any custom field to display');
                break;
            }

            input.push({
                entityMetadataId,
                label,
                type,
                displayed,
                active,
                taxable,
                ...(defaultValues?.length > 0 ? {
                    defaultValues: defaultValues.map((d) => ({ ...d, defaultValue: !StringUtils.isEmpty(d.defaultValue) ? d.defaultValue : '0' })),
                } : {}),
            });
        }

        if (failure) return;
        if (isPacks) {
            // Save pack cost in lot settings
            const record = packsCustomFields.find(({ notCustomField = false }) => notCustomField);
            const { defaultValues } = record;
            const toSave = lotsSettings.map((l) => ({ ...l, packCost: Number((defaultValues ?? []).find((c) => c.lotId === l.lotId)?.defaultValue ?? 0) }));
            await Promise.all(
                toSave
                    .map((r) => saveLotPackCost({
                        variables: {
                            lotId: r.lotId,
                            cost: r.packCost,
                        },
                    })),
            );

            refetchUserInformation();
            savePacksMetadata({
                variables: {
                    input,
                },
            });

            return;
        }

        saveMetadata({
            variables: {
                input,
            },
        });
    };

    const saveUserTabs = () => {
        const defaultLot = userInformation?.defaultLot || userInformation?.usersLot;
        const input = [
            {
                value: invUserTabName1 || '',
                key: INV_USER_TAB_NAME_1,
                lotName: defaultLot,
            },
            {
                value: invUserTabName2 || '',
                key: INV_USER_TAB_NAME_2,
                lotName: defaultLot,
            },
        ];

        saveUserTabNames({
            variables: {
                input,
            },
        });
    };

    const renderDefaultValueElement = (record, subSection) => {
        const { type, defaultValues } = record;
        const currentValue = (defaultValues ?? []).find((item) => item.lotId === selectedLot)?.defaultValue ?? 0;

        switch (type) {
        case DataType.MONEY.toUpperCase():
            return (
                <InputNumber
                    showCurrency={false}
                    value={currentValue}
                    onBlur={(e) => onChange({ target: { name: 'defaultValue', value: e.target.value, entity: record.entityMetadataId } }, false, subSection)}
                    placeholder="0"
                    decimalScale={2}
                    fixedDecimalScale
                    size="sm"
                />
            );
        default:
            return null;
        }
    };

    const renderTypeDropdown = (record) => {
        const options = [
            {
                key: uuid(),
                text: 'Alphanumeric',
                value: DataType.STRING.toUpperCase(),
            },
            {
                key: uuid(),
                text: 'Money',
                value: DataType.MONEY.toUpperCase(),
            },
            {
                key: uuid(),
                text: 'Date',
                value: DataType.DATE.toUpperCase(),
            },
            {
                key: uuid(),
                text: 'Dropdown',
                value: DataType.COMBO.toUpperCase(),
            },
        ];

        return (
            <TextField
                select
                name={`${record.property}-type`}
                value={record.type || ''}
                onChange={(e) => onChange(e)}
            >
                {options.map((item) => (
                    <MenuItem key={item.key} value={item.value}>
                        {item.text}
                    </MenuItem>
                ))}
            </TextField>
        );
    };

    const getColumns = (subSection) => {
        const columns = [
            {
                headerClassName: classes.tableHeader,
                label: '',
                dataKey: 'number',
                width: 50,
                // eslint-disable-next-line react/prop-types
                cellRenderer: ({ rowIndex }) => (
                    <span>{rowIndex + 1}</span>
                ),
            },
            {
                headerClassName: classes.tableHeader,
                label: 'Label',
                dataKey: 'label',
                width: 300,
                cellRenderer: (cell) => {
                    const { rowData: record } = cell;
                    const isPacks = subSection === 'packs';
                    if (isPacks) {
                        const { defaultValues, label, notCustomField = false } = record;
                        const currentValue = notCustomField ? label : ((defaultValues ?? []).find((item) => item.lotId === selectedLot)?.label ?? '');

                        return (
                            (
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    name="defaultLabel"
                                    disabled={notCustomField}
                                    value={currentValue}
                                    onChange={(e) => onChange(
                                        { target: { name: 'defaultLabel', value: e.target.value, entity: record.entityMetadataId } },
                                        false,
                                        subSection,
                                    )}
                                />
                            )
                        );
                    }

                    return (
                        (
                            <Form.Control
                                type="text"
                                size="sm"
                                name={`${record.property}-label`}
                                value={record.label}
                                onChange={(e) => onChange(e, false, subSection)}
                            />
                        )
                    );
                },
            },
        ];

        if ((isDeals || isInventory) && subSection !== 'packs') {
            columns.splice(
                1,
                0,
                {
                    headerClassName: classes.tableHeader,
                    label: 'Display',
                    dataKey: 'display',
                    width: 80,
                    cellRenderer: (cell) => {
                        const { rowData: record } = cell;
                        const { notCustomField = false } = record;

                        return (
                            <Checkbox
                                color="primary"
                                value="selected"
                                disabled={notCustomField}
                                name={`${record.property}-displayed`}
                                style={{ padding: 0 }}
                                checked={record.displayed}
                                onChange={(e) => onChange(e, false, subSection)}
                                inputProps={{
                                    'aria-label': 'primary checkbox',
                                }}
                            />
                        );
                    },
                },
            );

            columns.splice(
                2,
                0,
                {
                    headerClassName: classes.tableHeader,
                    label: 'Type',
                    dataKey: 'type',
                    width: 300,
                    cellRenderer: (cell) => {
                        const { rowData: record } = cell;
                        return renderTypeDropdown(record);
                    },
                },
            );
        }

        if (isInventory && subSection === 'packs') {
            columns.push(
                {
                    headerClassName: classes.tableHeader,
                    label: 'Default Value',
                    dataKey: 'defaultValue',
                    width: 150,
                    cellRenderer: (cell) => {
                        const { rowData: record } = cell;
                        return renderDefaultValueElement(record, subSection);
                    },
                },
            );
        }

        if (isService) {
            columns.splice(
                1,
                0,
                {
                    headerClassName: classes.tableHeader,
                    label: 'Active',
                    dataKey: 'active',
                    width: 80,
                    cellRenderer: (cell) => {
                        const { rowData: record } = cell;
                        return (
                            <Checkbox
                                color="primary"
                                value="selected"
                                name={`${record.property}-active`}
                                style={{ padding: 0 }}
                                checked={record.active}
                                onChange={(e) => onChange(e, false, subSection)}
                                inputProps={{
                                    'aria-label': 'primary checkbox',
                                }}
                            />
                        );
                    },
                },
            );

            columns.splice(
                2,
                0,
                {
                    headerClassName: classes.tableHeader,
                    label: 'Taxable',
                    dataKey: 'taxable',
                    width: 80,
                    cellRenderer: (cell) => {
                        const { rowData: record } = cell;
                        return (
                            <Checkbox
                                color="primary"
                                value="selected"
                                name={`${record.property}-taxable`}
                                style={{ padding: 0 }}
                                checked={record.taxable}
                                onChange={(e) => onChange(e, false, subSection)}
                                inputProps={{
                                    'aria-label': 'primary checkbox',
                                }}
                            />
                        );
                    },
                },
            );

            columns.push(
                {
                    headerClassName: classes.tableHeader,
                    label: (
                        <div className={classes.lotSelector}>
                            Default Value for:
                            <TextField
                                select
                                name="selectedLot"
                                value={selectedLot ?? ''}
                                onChange={(e) => onChange(e, false, subSection)}
                            >
                                {lots.map((item) => (
                                    <MenuItem key={item.lotId} value={item.lotId}>
                                        {item.lotName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </div>
                    ),
                    dataKey: 'defaultValue',
                    width: 300,
                    cellRenderer: (cell) => {
                        const { rowData: record } = cell;
                        return renderDefaultValueElement(record, subSection);
                    },
                },
            );
        }

        return columns;
    };

    return (
        <>
            {isInventory && (
                <Grid container className={classes.box}>
                    <Grid item>
                        <Form.Group as={Col}>
                            <Form.Label className={classes.labels}>Custom User Tabs</Form.Label>
                        </Form.Group>
                    </Grid>
                    <Grid container spacing={1}>
                        <Grid item lg={3} md={4} xs={6}>
                            <Form.Group as={Col}>
                                <Form.Label className={classes.subLabel}>User Tab Name 1</Form.Label>
                                <Form.Control
                                    className={classes.input}
                                    type="text"
                                    name="invUserTabName1"
                                    value={invUserTabName1}
                                    onChange={(e) => onChange(e)}
                                />
                            </Form.Group>
                        </Grid>
                        <Grid item lg={3} md={4} xs={6}>
                            <Form.Group as={Col}>
                                <Form.Label className={classes.subLabel}>User Tab Name 2</Form.Label>
                                <Form.Control
                                    className={classes.input}
                                    type="text"
                                    name="invUserTabName2"
                                    value={invUserTabName2}
                                    onChange={(e) => onChange(e)}
                                />
                            </Form.Group>
                        </Grid>
                    </Grid>
                    <If condition={writable}>
                        <Grid container>
                            <Grid item lg={6} md={8} xs={12}>
                                <Form.Group className={classes.customTabsSave} as={Col}>
                                    <Button
                                        className={classes.containedSecondaryInfo}
                                        size="small"
                                        disabled={
                                            StringUtils.isEmpty(invUserTabName1)
                                            || StringUtils.isEmpty(invUserTabName2)
                                        }
                                        onClick={saveUserTabs}
                                    >
                                        Save
                                    </Button>
                                </Form.Group>
                            </Grid>
                        </Grid>
                    </If>
                </Grid>
            )}
            <Grid container className={isInventory ? classes.box : ''}>
                <Grid item xs={12}>
                    <Form.Group as={Col}>
                        <Form.Label className={classes.labels}>Custom Fields</Form.Label>
                    </Form.Group>
                </Grid>
                <Grid item lg={11} xs={12}>
                    <Form.Group as={Col}>
                        <div className={clsx(classes.tableContainer, 'virtual-table-base')}>
                            <VirtualTable
                                loading={metaDataLoading}
                                rowHeight={45}
                                totalRecords={customFields.length}
                                data={customFields}
                                columns={getColumns()}
                            />
                        </div>
                    </Form.Group>
                </Grid>
                <If condition={writable}>
                    <Grid item className={classes.customFieldsSave} lg={11} xs={12}>
                        <Grid item>
                            <Form.Group as={Col}>
                                <Button
                                    className={classes.containedSecondaryInfo}
                                    size="small"
                                    onClick={() => saveCustomFields()}
                                >
                                    Save
                                </Button>
                            </Form.Group>
                        </Grid>
                    </Grid>
                </If>
            </Grid>
            {isInventory && (
                <Grid container className={isInventory ? classes.box : ''}>
                    <Grid className={classes.packs} item lg={11} xs={12}>
                        <Form.Label className={classes.labels}>Custom Packs</Form.Label>
                        <TextField
                            select
                            name="selectedLot"
                            value={selectedLot ?? ''}
                            onChange={(e) => onChange(e)}
                        >
                            {lots.map((item) => (
                                <MenuItem key={item.lotId} value={item.lotId}>
                                    {item.lotName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <span className={classes.packsMessage}>
                        Packs will display automatically in the vehicle detail page only if there is a description typed in the &quot;Label&quot; field.
                        (Exception is if originally a label was existing when vehicle was added)
                    </span>
                    <Grid item lg={11} xs={12}>
                        <Form.Group as={Col}>
                            <div className={clsx(classes.tableContainer, 'virtual-table-base')}>
                                <VirtualTable
                                    loading={packsMetadataLoading}
                                    rowHeight={45}
                                    totalRecords={packsCustomFields.length}
                                    data={packsCustomFields}
                                    columns={getColumns('packs')}
                                />
                            </div>
                        </Form.Group>
                    </Grid>
                    <If condition={writable}>
                        <Grid item className={classes.customFieldsSave} lg={11} xs={12}>
                            <Grid item>
                                <Form.Group as={Col}>
                                    <Button
                                        className={classes.containedSecondaryInfo}
                                        size="small"
                                        onClick={() => saveCustomFields('packs')}
                                    >
                                        Save
                                    </Button>
                                </Form.Group>
                            </Grid>
                        </Grid>
                    </If>
                </Grid>
            )}
            <ConfirmDialog
                title="Attention!"
                description={`
                    Changing the Field Type may cause data issues.
                    Example: If you are using dollar amounts and change to Text, then you can’t get a subtotal for this field.
                    Continue?
                `}
                open={confirmDialogOpen}
                variant="outlined"
                titlePrimary="Yes"
                titleSecondary="Cancel"
                onClose={onConfirmDialogClose}
                onClickSecondary={onConfirmDialogClose}
                onClickPrimary={() => {
                    const { name, value } = tempCustomFieldData.current;
                    onChange({ target: { name, value } }, true);
                    onConfirmDialogClose();
                }}
            />
        </>
    );
};

CustomFieldsSettings.propTypes = {
    writable: PropTypes.bool.isRequired,
    module: PropTypes.string.isRequired,
};

export default CustomFieldsSettings;
